import React, { useEffect, useRef } from "react";
import { Check, X } from "lucide-react";

interface EditMessageBubbleProps {
  text: string;
  onTextChange: (text: string) => void;
  onSave: () => void;
  onCancel: () => void;
}

export const EditMessageBubble: React.FC<EditMessageBubbleProps> = ({
  text,
  onTextChange,
  onSave,
  onCancel,
}) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    // Focus and select all text when editing starts
    if (textareaRef.current) {
      textareaRef.current.focus();
      textareaRef.current.select();
    }
  }, []);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      onSave();
    } else if (e.key === "Escape") {
      e.preventDefault();
      onCancel();
    }
  };

  return (
    <div className="group/message flex w-full flex-col items-end gap-2">
      <div className="flex w-full justify-end">
        <div className="relative w-fit max-w-[80%] rounded-3xl border-2 border-primary/30 bg-primary/5 p-3 text-left leading-relaxed text-foreground">
          <textarea
            ref={textareaRef}
            value={text}
            onChange={(e) => onTextChange(e.target.value)}
            onKeyDown={handleKeyDown}
            className="w-full min-h-[60px] bg-transparent text-base leading-7 resize-none outline-none"
            placeholder="Edit your message..."
            rows={3}
          />
        </div>
      </div>
      
      {/* Action buttons */}
      <div className="flex items-center gap-2">
        <button
          onClick={onSave}
          disabled={!text.trim()}
          className="flex items-center gap-1 rounded-full bg-primary px-3 py-1.5 text-xs text-primary-foreground transition-colors hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
          aria-label="Save changes"
        >
          <Check className="h-3 w-3" />
          Save
        </button>
        <button
          onClick={onCancel}
          className="flex items-center gap-1 rounded-full bg-muted px-3 py-1.5 text-xs text-muted-foreground transition-colors hover:bg-muted/80"
          aria-label="Cancel editing"
        >
          <X className="h-3 w-3" />
          Cancel
        </button>
      </div>
    </div>
  );
};
